import { DailyRecord, SECTOR_WEIGHTS, AStockData } from '@/types/sector';

/**
 * 累计计算每个板块的总得分
 * 第一名 +50、第二名 +30、第三名 +20
 */
export function calculateSectorScores(dailyData: DailyRecord[]): Record<string, number> {
  const scores: Record<string, number> = {};
  for (const item of dailyData) {
    scores[item.first] = (scores[item.first] ?? 0) + SECTOR_WEIGHTS.first;
    scores[item.second] = (scores[item.second] ?? 0) + SECTOR_WEIGHTS.second;
    scores[item.third] = (scores[item.third] ?? 0) + SECTOR_WEIGHTS.third;
  }
  return scores;
}

/** 按日期升序排序（不修改原数组） */
export function sortByDate(data: DailyRecord[]): DailyRecord[] {
  return [...data].sort((a, b) => a.date.localeCompare(b.date));
}

/** 按日期降序排序（不修改原数组） */
export function sortByDateDesc(data: DailyRecord[]): DailyRecord[] {
  return [...data].sort((a, b) => b.date.localeCompare(a.date));
}

/** 过滤最近 N 天的数据（按今日为锚点） */
export function filterRecentDays(data: DailyRecord[], days: number, now = new Date()): DailyRecord[] {
  const cutoff = new Date(now);
  cutoff.setDate(cutoff.getDate() - days);
  const cutoffStr = cutoff.toISOString().slice(0, 10);
  return data.filter((d) => d.date >= cutoffStr);
}

/** 过滤最近 N 个月的数据 */
export function filterRecentMonths(data: DailyRecord[], months: number, now = new Date()): DailyRecord[] {
  const cutoff = new Date(now);
  cutoff.setMonth(cutoff.getMonth() - months);
  const cutoffStr = cutoff.toISOString().slice(0, 10);
  return data.filter((d) => d.date >= cutoffStr);
}

/**
 * 累计得分序列：返回每个板块在每个日期的累计得分
 */
export function buildCumulativeSeries(
  sortedAsc: DailyRecord[],
  topSectors: string[],
): Record<string, number[]> {
  const result: Record<string, number[]> = {};
  const cumulative: Record<string, number> = {};
  topSectors.forEach((s) => (cumulative[s] = 0));

  for (const record of sortedAsc) {
    if (topSectors.includes(record.first)) cumulative[record.first] += SECTOR_WEIGHTS.first;
    if (topSectors.includes(record.second)) cumulative[record.second] += SECTOR_WEIGHTS.second;
    if (topSectors.includes(record.third)) cumulative[record.third] += SECTOR_WEIGHTS.third;

    for (const sector of topSectors) {
      if (!result[sector]) result[sector] = [];
      result[sector].push(cumulative[sector]);
    }
  }
  return result;
}

/** N 天内的增长（近 7 天涨幅） */
export function weeklyGrowth(
  series: Record<string, number[]>,
  days = 7,
): Record<string, number> {
  const result: Record<string, number> = {};
  for (const [sector, points] of Object.entries(series)) {
    if (points.length === 0) {
      result[sector] = 0;
      continue;
    }
    const end = points[points.length - 1];
    const startIdx = Math.max(0, points.length - 1 - days);
    const start = points[startIdx];
    result[sector] = end - start;
  }
  return result;
}

/** Top N 板块（按得分降序） */
export function topNSectors(scores: Record<string, number>, n: number): string[] {
  return Object.entries(scores)
    .sort((a, b) => b[1] - a[1])
    .slice(0, n)
    .map(([s]) => s);
}

/** 重算整个数据快照（用于变更后归一化） */
export function refreshSnapshot(dailyData: DailyRecord[]): AStockData {
  return {
    dailyData: sortByDate(dailyData),
    sectorScores: calculateSectorScores(dailyData),
  };
}
